import {
  FormCheckBox,
  Loader,
  FormInputField,
  StyledMessage,
} from '@/components'
import { ThemeColor } from '@/enums'
import { StyledLinkTypography } from '@/pages/SelfAssessmentPage/steps/styles'
import { FormProvider, useContactModal, useFormContext } from '@/providers'
import {
  usePublicCheckoutOverview,
  usePublicCheckoutPaymentMethod,
  useSavePublicCheckoutPaymentMethod,
  useUpdatePublicCheckoutPaymentMethod,
} from '@/services'
import { theme } from '@/theme'
import { useLanguage } from '@/utils'
import { Alert } from '@surein/ui'
import { Calendar, FileText, InfoIcon, Lock } from 'lucide-react'
import { useEffect, ReactElement } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { makePaymentsValidationSchema } from './validationSchema'

const StyledLink = styled(StyledLinkTypography)`
  font-weight: 700;
`

const useMessages = (): Record<string, string> => {
  const { t } = useTranslation()
  return {
    general: t('validations.general'),
    sepa: t('sepa'),
    invalidIban: t('profile.payments.invalidIban'),
    noneGermanIban: t('profile.payments.noneGermanIban'),
    authorizeSepa: t('profile.payments.authorizeSepaErrorLabel'),
  }
}

interface FormValues {
  name: string
  iban: string
  sepa: boolean
  paymentId?: string
}

interface PaymentMethodFormProps {
  setButtonState: (state: { onClick?: () => void; loading?: boolean }) => void
  startDate: number
}

const PaymentMethodForm = ({
  setButtonState,
  startDate,
}: PaymentMethodFormProps) => {
  const { t } = useTranslation()
  const messages = useMessages()
  const language = useLanguage()
  const { openContact } = useContactModal()

  const { submitForm, errors, customValidate } = useFormContext<FormValues>()

  useEffect(() => {
    setButtonState({
      onClick: () =>
        customValidate({
          onSuccess: submitForm,
        }),
    })
  }, [])

  const date = new Intl.DateTimeFormat(language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(startDate)

  return (
    <div className="flex flex-col gap-4">
      {errors?.iban && (
        <Alert
          icon={InfoIcon}
          iconAlignment="top"
          // @ts-expect-error change type in Alert toolkit
          text={
            <Trans
              components={{
                link: <StyledLink onClick={openContact} variant="captionB" />,
              }}
              i18nKey={t('profile.payments.ibanAlertLabel')}
              variant="danger"
            />
          }
          variant="danger"
        />
      )}

      <div className="flex flex-col gap-4">
        <FormInputField
          infoMessage={t('profile.payments.firstPaymentDateLabel', {
            date,
          })}
          label={t('profile.payments.name.label')}
          name="name"
        />
        <FormInputField
          label={t('profile.payments.iban.label')}
          name="iban"
          showValidCheck
          validateOnChange
        />

        {errors?.sepa && (
          <Alert
            icon={InfoIcon}
            iconAlignment="top"
            text={messages.authorizeSepa}
            variant="danger"
          />
        )}

        <FormCheckBox
          id="sepa"
          label={messages.sepa}
          name="sepa"
          ignoreErrorKeys={[]}
          noMargin={false}
        />
      </div>
    </div>
  )
}

const StaticPaymentInfo = (): ReactElement => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1">
        <FileText size={16} />
        <StyledMessage color={ThemeColor.b100}>
          {t('profile.payments.getCertificateInfoLabel')}
        </StyledMessage>
      </div>
      <div className="flex gap-1">
        <Calendar size={16} />
        <StyledMessage color={ThemeColor.b100}>
          {t('profile.payments.cancelInfoLabel')}
        </StyledMessage>
      </div>
      <div className="flex gap-1">
        <Lock color={theme.color[ThemeColor.success1]} size={16} />
        <StyledMessage color={ThemeColor.success1}>
          {t('profile.payments.securedInfoLabel')}
        </StyledMessage>
      </div>
    </div>
  )
}

interface OverviewData {
  startDate: number
  recommendationProductId?: string
  [key: string]: any
}

interface PublicPaymentMethodStepProps {
  onNext: () => void
  setButtonState: (state: { onClick?: () => void; loading?: boolean }) => void
}

export const PublicPaymentMethodStep = ({
  onNext,
  setButtonState,
}: PublicPaymentMethodStepProps) => {
  const messages = useMessages()
  const { data, isLoading } = usePublicCheckoutPaymentMethod()
  const { data: rawOverviewData } = usePublicCheckoutOverview()

  // Define typed overview data with fallback for startDate
  const overviewData: OverviewData = (rawOverviewData as OverviewData) || {
    startDate: Date.now(),
  }

  const createPaymentMethodMutation = useSavePublicCheckoutPaymentMethod()
  const updatePaymentMethodMutation = useUpdatePublicCheckoutPaymentMethod(
    data?.paymentId || '',
  )

  const handleSubmit = ({ name, iban }: { name: string; iban: string }) => {
    const mutation = data?.paymentId
      ? updatePaymentMethodMutation
      : createPaymentMethodMutation

    mutation.mutate({ name, iban }, { onSuccess: onNext })
  }

  useEffect(() => {
    setButtonState({
      loading:
        createPaymentMethodMutation.isLoading ||
        updatePaymentMethodMutation.isLoading,
    })
  }, [
    createPaymentMethodMutation.isLoading,
    updatePaymentMethodMutation.isLoading,
  ])

  if (isLoading) return <Loader />

  // Ensure startDate has a default value
  const startDate = overviewData?.startDate || Date.now()

  return (
    <div className="flex flex-col gap-6">
      <FormProvider
        initialValues={{ ...data, sepa: Boolean(data?.paymentId) }}
        onSubmit={handleSubmit}
        validationSchema={makePaymentsValidationSchema(messages)}
      >
        <PaymentMethodForm
          setButtonState={setButtonState}
          startDate={startDate}
        />
      </FormProvider>
      <StaticPaymentInfo />
    </div>
  )
}
